import '#lit/components/base/modal/index.js';

import {html} from 'lit';
import type {
  Cmd,
  ImmutableProgram,
  InitialProperties,
  ImmutableResult,
  UpdateHandler,
} from '#lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
} from '#lit/components/mixins/immutable_component/index.js';
import type {VariantsFor} from '#lit/types/architecture.js';
import type {TemplateParameters} from '@typeface-ai/typeface-embed-react/lib/esm/model.js';
import {
  TypefaceEmbedUseCase,
  TypefaceEmbedIDP,
  TypefaceEmbedOutputFormat,
  GOOGLE_AD_TEMPLATE,
} from '@typeface-ai/typeface-embed-react/lib/esm/model.js';
import {getEmbedUrl} from '@typeface-ai/typeface-embed-react/lib/esm/utils.js';
import type {TypefaceGeneratedJson} from '#lit/types/integrations/typeface.js';
import type TypefaceModal from './index.js';

const APPLY_BUTTON_NAME = 'Apply';
const CANCEL_BUTTON_NAME = 'Cancel';
const TYPEFACE_ORIGIN_URL = 'https://app.typeface.ai';
const SIGN_ON_URL = undefined;
const AUTH_PROVIDER = false;
const HIDE_HEADER = false;
const TYPE_FACE_STYLES = undefined;

enum TypefaceIframeEventType {
  APPLY = 'tf-embed-generated-content',
  CANCEL = 'tf-embed-cancel',
}

enum TypefaceModalEventKey {
  APPLIED = 'applied',
  CANCELLED = 'cancelled',
}

type TypefaceIframeMessageEventData = {
  type: TypefaceIframeEventType;
  data: string;
};

export type Model = {
  open: boolean;
  useCase: TypefaceEmbedUseCase;
  outputFormat: TypefaceEmbedOutputFormat;
  parameters: TemplateParameters;
};

export const Msg = makeVariants({
  open: Variant<void>()('open'),
  close: Variant<void>()('close'),
  setParameters: Variant<Model['parameters']>()('set-parameters'),
});

export type Msg = VariantsFor<typeof Msg>;

function eventDispatcher(host: TypefaceModal) {
  return {
    emit(eventKey: TypefaceModalEventKey, detail?: TypefaceGeneratedJson) {
      const event = new CustomEvent(eventKey, {detail});

      host.dispatchEvent(event);
      host.handleUpdate(Msg.close());
    },
  };
}

// -- Init

function isSameIframeOrigin(
  event: MessageEvent,
  iframe: HTMLIFrameElement | undefined
): boolean {
  return !!(iframe && event.source === iframe.contentWindow);
}

function attachEventListeners(host: TypefaceModal) {
  const dispatcher = eventDispatcher(host);

  addEventListener(
    'message',
    (e: MessageEvent<TypefaceIframeMessageEventData>) => {
      if (!isSameIframeOrigin(e, host.$iframe)) return;
      switch (e.data.type) {
        case TypefaceIframeEventType.APPLY: {
          dispatcher.emit(
            TypefaceModalEventKey.APPLIED,
            JSON.parse(e.data.data) as TypefaceGeneratedJson
          );
          break;
        }

        case TypefaceIframeEventType.CANCEL:
          dispatcher.emit(TypefaceModalEventKey.CANCELLED);
          break;
      }
    },
    false
  );
}

function init(props: InitialProperties, cmd: Cmd): ImmutableResult<Model> {
  const model: Model = {
    open: <boolean>props.get('open') ?? false,
    useCase:
      <TypefaceEmbedUseCase>props.get('useCase') ??
      TypefaceEmbedUseCase.Template,
    outputFormat:
      <TypefaceEmbedOutputFormat>props.get('output') ??
      TypefaceEmbedOutputFormat.BLOCKS,
    parameters: <Model['parameters']>props.get('parameters') ?? {
      template: GOOGLE_AD_TEMPLATE,
    },
  };

  return [model, cmd(attachEventListeners)];
}

// -- Update

function update(msg: Msg, model: Model, cmd: Cmd): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'open':
      return {...model, open: true} as const;
    case 'close':
      return [
        {...model, open: false},
        cmd((host: TypefaceModal) => host.reload()),
      ] as const;
    case 'set-parameters':
      return [
        {...model, parameters: msg.value},
        cmd((host: TypefaceModal) => host.reload()),
      ] as const;
  }
}

// -- View

function viewIframe(model: Model) {
  const url = getEmbedUrl(
    TypefaceEmbedIDP.Typeface,
    model.useCase,
    SIGN_ON_URL,
    TYPEFACE_ORIGIN_URL,
    SIGN_ON_URL,
    APPLY_BUTTON_NAME,
    CANCEL_BUTTON_NAME,
    model.parameters,
    model.outputFormat,
    AUTH_PROVIDER,
    HIDE_HEADER,
    TYPE_FACE_STYLES
  );

  return html`
    <iframe id="typeface-iframe" src=${url} loading="lazy"></iframe>
  `;
}

function view(model: Model, handleUpdate: UpdateHandler<Model, Msg>) {
  return html`
    <fly-modal ?open=${model.open} @closed=${() => handleUpdate(Msg.close())}>
      <div class="typeface-wrapper">${viewIframe(model)}</div>
    </fly-modal>
  `;
}

// -- Program

export const main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view,
};
